import React, { useEffect, useState, useRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Plus, AlertCircle, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

import { taskAPI, innovatorAPI } from "../../services/api";
import { useWallet } from "../../context/WalletContext";
import { TaskCard } from "../kanban-board/task-components/TaskCard";
import { TaskDetailModal } from "../kanban-board/task-components/TaskDetailModal";
import { STATUS_STYLES, PERMISSION_TYPES, Drawer, StatusChip } from "../kanban-board/kanban-board";

// Define columns for user task board - different from project kanban board
const USER_COLUMNS = [
  { id: "created", title: "Created", mappedStatuses: ["backlog", "open"] },
  { id: "applied", title: "Application Submitted", mappedStatuses: [] }, // Will be populated with tasks user has applied to
  { id: "in_progress", title: "In Progress", mappedStatuses: ["in_progress"] },
  { id: "submitted", title: "Submitted", mappedStatuses: ["submitted"] },
  { id: "approved", title: "Approved", mappedStatuses: ["approved"] },
  { id: "rejected", title: "Rejected", mappedStatuses: ["rejected"] },
];

// Define the column container component
function ColumnContainer({ id, children }) {
  const { setNodeRef } = useDroppable({
    id,
    data: { columnId: id },
  });

  return (
    <div
      ref={setNodeRef}
      className="flex w-72 min-w-72 flex-col gap-4 rounded-xl bg-gray-50 p-3 border border-gray-100 shadow-sm mx-1"
    >
      {children}
    </div>
  );
}

// Custom task card to include project name
function UserTaskCard({ task, columnId, onClick, dragHandle = false }) {
  const project = task.projectId;

  return (
    <div
      onClick={() => onClick?.(task)}
      className="cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Project name badge */}
      <div className="mb-2">
        <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
          {project?.title || "Unknown Project"}
        </span>
      </div>

      <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">{task.title}</h3>
      <p className="text-xs text-gray-500 line-clamp-2 mb-2">{task.description}</p>

      <div className="flex items-center justify-between">
        <StatusChip status={task.status} />
        <span className="text-xs font-medium text-indigo-600">{task.taskPoints} IU%</span>
      </div>
    </div>
  );
}


export default function UserTaskBoardTab({ walletOverride }) {
  const { walletAddress, isConnected } = useWallet();
  const safeWalletAddress = walletOverride || walletAddress || "";
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState({});
  const [innovators, setInnovators] = useState([]);
  const [detailTask, setDetailTask] = useState(null);
  const [activeId, setActiveId] = useState(null);
  
  // States for task detail modal
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const [subsOpen, setSubsOpen] = useState(false);
  const [appsModal, setAppsModal] = useState(false);
  const [subsModal, setSubsModal] = useState(false);
  const [showAppForm, setShowAppForm] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  // Fetch user tasks
  useEffect(() => {
  if (!safeWalletAddress) {
    setLoading(false);
    return;
  }

  async function fetchUserTasks() {
    try {
      setLoading(true);
      console.log("Fetching tasks for user:", safeWalletAddress);

      const { data } = await taskAPI.getUserTasks(safeWalletAddress);
      console.log("Fetched tasks:", data);

      setTasks(data);

      // Build projects mapping directly from populated `projectId`
      const projectsData = {};
      data.forEach(task => {
        const project = task.projectId;
        if (project && typeof project === "object" && project._id) {
          projectsData[project._id] = project;
        }
      });

      setProjects(projectsData);
    } catch (err) {
      console.error("Error fetching user tasks:", err);
      setError(err.message || "Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }

  fetchUserTasks();
}, [safeWalletAddress]);

  // Fetch innovators once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await innovatorAPI.getAll();
        setInnovators(data);
      } catch (error) {
        console.error("Error fetching innovators:", error);
        setInnovators([]);
      }
    })();
  }, []);

  // Scroll handlers
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 20);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 20);
    
    if (!hasScrolled && scrollLeft > 0) {
      setHasScrolled(true);
      setShowScrollHint(false);
    }
  };

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      handleScroll();
      
      const checkOverflow = () => {
        if (scrollContainer.scrollWidth > scrollContainer.clientWidth) {
          setShowRightArrow(true);
        } else {
          setShowRightArrow(false);
        }
      };
      
      const resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(scrollContainer);
      
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [tasks]);

  // Show scroll hint for new users
  useEffect(() => {
    if (tasks.length > 0 && !hasScrolled) {
      const checkScrollable = () => {
        if (!scrollContainerRef.current) return;
        const { scrollWidth, clientWidth } = scrollContainerRef.current;
        if (scrollWidth > clientWidth) {
          setShowScrollHint(true);
          
          const timer = setTimeout(() => {
            setShowScrollHint(false);
          }, 5000);
          
          return () => clearTimeout(timer);
        }
      };
      
      setTimeout(checkScrollable, 100);
    }
  }, [tasks, hasScrolled]);

  // Update detailTask if it exists in the fetched tasks
  useEffect(() => {
    setDetailTask((currentDetailTask) => {
      if (!currentDetailTask) return null;
      const updatedTask = tasks.find(
        (task) => task._id === currentDetailTask._id
      );
      return updatedTask || null;
    });
  }, [tasks]);

  // Group tasks by user column status
  const tasksByUserColumn = USER_COLUMNS.reduce((acc, column) => {
    acc[column.id] = [];
    
    // Handle special columns
    if (column.id === 'created') {
      // Tasks created by this user
      acc[column.id] = tasks.filter(t => 
        t.createdBy?.walletAddress?.toLowerCase() === safeWalletAddress.toLowerCase() 
      );
    } else if (column.id === 'applied') {
      // Tasks where user has applied
      acc[column.id] = tasks.filter(t => 
        t.applicants?.some(app => 
          app.contributorWalletAddress?.toLowerCase() === safeWalletAddress.toLowerCase() &&
          app.status === 'pending'
        ) &&
        t.status === 'open'
      );
    } else if (column.id === 'in_progress') {
      // Tasks where user is the assignee
      acc[column.id] = tasks.filter(t => 
        t.assigneeWalletAddress?.toLowerCase() === safeWalletAddress.toLowerCase() &&
        t.status === 'in_progress'
      );
    } else if (column.id === 'submitted') {
      // Tasks where user has submitted work
      acc[column.id] = tasks.filter(t => 
        t.submissions?.some(sub => 
          sub.contributorWalletAddress?.toLowerCase() === safeWalletAddress.toLowerCase() &&
          sub.status === 'pending'
        ) &&
        t.status === 'submitted'
      );
    } else if (column.id === 'approved') {
      // Tasks where user's work was approved
      acc[column.id] = tasks.filter(t => 
        t.submissions?.some(sub => 
          sub.contributorWalletAddress?.toLowerCase() === safeWalletAddress.toLowerCase() &&
          sub.status === 'approved'
        ) &&
        t.status === 'approved'
      );
    } else if (column.id === 'rejected') {
      // Tasks where user's work was rejected
      acc[column.id] = tasks.filter(t => 
        t.submissions?.some(sub => 
          sub.contributorWalletAddress?.toLowerCase() === safeWalletAddress.toLowerCase() &&
          (sub.status === 'rejected' || t.status === 'rejected')
        )
      );
    }
    
    return acc;
  }, {});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  

  const handleDragStart = ({ active }) => {
    // Read-only board, no dragging functionality needed
    setActiveId(active.id);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    // This is a read-only board, so we don't need to implement status changes
  };

  // Helper function to generate etherscan links based on the current chain
  const getExplorerUrl = (txHash) => {
    // Default to Optimism Sepolia
    return `https://sepolia-optimism.etherscan.io/tx/${txHash}`;
  };

  // Refresh data after task interactions
  const refreshUserTasks = async () => {
    if (!isConnected || !safeWalletAddress) return;
    
    try {
      const { data } = await taskAPI.getUserTasks(safeWalletAddress);
      setTasks(data);
    } catch (err) {
      console.error("Error refreshing user tasks:", err);
    }
  };

  const onRespond = async (appId, status, feedback) => {
    await taskAPI.respondToApplication(appId, { status, feedback });
    refreshUserTasks();
  };

  // Replace the existing return with the full UI
  return (
    <>
      {loading ? (
        <div className="flex h-[80vh] items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-gray-500">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            <p>Loading your tasks...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex h-[80vh] items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-gray-500 max-w-md text-center">
            <AlertCircle className="h-8 w-8 text-amber-500" />
            <p className="text-lg font-medium">Error loading tasks</p>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        </div>
      ) 
    //   : !isConnected ? (
    //     <div className="flex h-[80vh] items-center justify-center">
    //       <div className="flex flex-col items-center gap-3 text-gray-500 max-w-md text-center p-6 bg-gray-50 rounded-xl border border-gray-200">
    //         <div className="bg-amber-100 p-3 rounded-full">
    //           <AlertCircle className="h-6 w-6 text-amber-600" />
    //         </div>
    //         <p className="text-lg font-medium text-gray-800">Wallet not connected</p>
    //         <p className="text-sm text-gray-500">
    //           Connect your wallet to view your tasks across all projects
    //         </p>
    //       </div>
    //     </div>
    //   ) 
      : tasks.length === 0 ? (
        <div className="flex h-[80vh] items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-gray-500 max-w-md text-center p-6 bg-gray-50 rounded-xl border border-gray-200">
            <div className="bg-indigo-100 p-3 rounded-full">
              <Plus className="h-6 w-6 text-indigo-600" />
            </div>
            <p className="text-lg font-medium text-gray-800">No tasks yet</p>
            <p className="text-sm text-gray-500">
              You haven't created or participated in any tasks yet. Start by applying to open tasks or creating tasks in your projects.
            </p>
          </div>
        </div>
      ) : (
        <div className="relative overflow-y-auto h-[80vh]">
          {/* Scroll hint overlay */}
          {showScrollHint && (
            <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
              <div className="absolute right-12 top-1/2 -translate-y-1/2 transform animate-pulse bg-white/90 rounded-lg shadow-lg py-2 px-4 max-w-[200px] text-center">
                <div className="text-sm font-medium text-gray-800">Scroll to see more</div>
                <div className="text-xs text-gray-500 mt-1">Swipe horizontally or use arrow buttons</div>
                <div className="flex justify-center mt-2">
                  <ChevronLeft className="h-4 w-4 text-gray-400" />
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
          )}
          
          {/* Left scroll arrow */}
          {showLeftArrow && (
            <button 
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-r-lg p-2 text-gray-600 hover:text-gray-900 border border-gray-200 border-l-0 transition-all duration-200 touch-manipulation"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-6 w-6" />
              <div className="absolute left-full top-1/2 -translate-y-1/2 h-12 w-6 bg-gradient-to-r from-white/80 to-transparent pointer-events-none" />
            </button>
          )}
          
          {/* Right scroll arrow */}
          {showRightArrow && (
            <button 
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-l-lg p-2 text-gray-600 hover:text-gray-900 border border-gray-200 border-r-0 transition-all duration-200 touch-manipulation"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-6 w-6" />
              <div className="absolute right-full top-1/2 -translate-y-1/2 h-12 w-6 bg-gradient-to-l from-white/80 to-transparent pointer-events-none" />
            </button>
          )}
          
          {/* Mobile scroll indicators at bottom */}
          <div className="absolute bottom-1 left-0 right-0 flex justify-center items-center z-10 sm:hidden">
            <div className="bg-white/80 rounded-full px-3 py-1 shadow-md border border-gray-200">
              <div className="flex items-center gap-1">
                <div className={`h-2 w-2 rounded-full ${showLeftArrow ? 'bg-indigo-500' : 'bg-gray-300'}`}></div>
                <div className="h-2 w-8 rounded-full bg-gray-300"></div>
                <div className={`h-2 w-2 rounded-full ${showRightArrow ? 'bg-indigo-500' : 'bg-gray-300'}`}></div>
              </div>
            </div>
          </div>
          
          {/* Kanban board with scroll container */}
          <div
            ref={scrollContainerRef}
            className="flex h-[80vh] gap-2 overflow-x-auto py-2 pb-4 px-3 scroll-smooth hide-scrollbar"
            style={{ overscrollBehavior: "contain" }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              {USER_COLUMNS.map((col) => (
                <ColumnContainer id={col.id} key={col.id}>
                  <header className="mb-3 flex items-center justify-between px-1">
                    <span
                      className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-white 
                        ${col.id === 'created' ? 'bg-slate-400' : 
                          col.id === 'applied' ? 'bg-sky-400' : 
                          col.id === 'in_progress' ? 'bg-violet-400' : 
                          col.id === 'submitted' ? 'bg-amber-400' : 
                          col.id === 'approved' ? 'bg-teal-400' : 'bg-rose-400'}`}
                    >
                      {/* coloured dot */}
                      <span
                        className={`h-2 w-2 rounded-full 
                          ${col.id === 'created' ? 'bg-slate-200' : 
                            col.id === 'applied' ? 'bg-sky-200' : 
                            col.id === 'in_progress' ? 'bg-violet-200' : 
                            col.id === 'submitted' ? 'bg-amber-200' : 
                            col.id === 'approved' ? 'bg-teal-200' : 'bg-rose-200'}`}
                      />
                      <span>{col.title}</span>
                    </span>

                    <span className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-200 text-xs font-medium text-gray-700">
                      {tasksByUserColumn[col.id].length}
                    </span>
                  </header>

                  <SortableContext
                    items={tasksByUserColumn[col.id].map((t) => t._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="flex h-full flex-col gap-3 overflow-y-auto pr-1">
                      {tasksByUserColumn[col.id].map((task) => (
                        <UserTaskCard
                          key={task._id}
                          task={task}
                          project={projects[typeof task.projectId === "string" ? task.projectId : task.projectId._id]}

                          columnId={col.id}
                          onClick={setDetailTask}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </ColumnContainer>
              ))}

              {/* overlay keeps dragged card above scroll containers */}
              <DragOverlay dropAnimation={null}>
                {activeId ? (
                  <UserTaskCard
                    task={tasks.find((t) => t._id === activeId) || { title: "" }}
                    project={projects[tasks.find((t) => t._id === activeId)?.projectId?._id]}

                    columnId={""}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      )}

      {/* Task detail modal */}
      {detailTask && (
        <TaskDetailModal
          open={Boolean(detailTask)}
          onClose={() => {
            setDetailTask(null);
            setShowSubmitForm(false);
            setAppsOpen(false);
            setSubsOpen(false);
            setAppsModal(false);
            setSubsModal(false);
            setSelectedApplication(null);
            setSelectedSubmission(null);
          }}
          task={detailTask}
          project={projects[detailTask.projectId]}
          walletAddress={safeWalletAddress}
          innovators={innovators}
          onTaskUpdated={refreshUserTasks}
          onOpenApplication={() => setAppsModal(!appsModal)}
          onOpenSubmission={() => setShowSubmitForm(!showSubmitForm)}
          isCreator={detailTask?.createdBy?.walletAddress?.toLowerCase() === safeWalletAddress.toLowerCase()}
          onRespond={onRespond}
          showSubmitForm={showSubmitForm}
          setShowSubmitForm={setShowSubmitForm}
          appsOpen={appsOpen}
          setAppsOpen={setAppsOpen}
          subsOpen={subsOpen}
          setSubsOpen={setSubsOpen}
          appsModal={appsModal}
          setAppsModal={setAppsModal}
          subsModal={subsModal}
          showAppForm={showAppForm}
          setShowAppForm={setShowAppForm}
          selectedApplication={selectedApplication}
          setSelectedApplication={setSelectedApplication}
          selectedSubmission={selectedSubmission}
          setSelectedSubmission={setSelectedSubmission}
          setSubsModal={setSubsModal}
          getExplorerUrl={getExplorerUrl}
        />
      )}
    </>
  );
}
